import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    ChevronLeft, BarChart3, TrendingUp, DollarSign,
    Package, Plus, AlertTriangle, RefreshCw, Archive
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const EventBarDashboard = () => {
    const { slug, eventId } = useParams();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Modal para cargar stock
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [addQuantity, setAddQuantity] = useState('');
    const [submittingStock, setSubmittingStock] = useState(false);

    useEffect(() => {
        fetchData();

        // Auto refresh cada 30 segundos
        const interval = setInterval(fetchStatsOnly, 30000);
        return () => clearInterval(interval);
    }, [slug, eventId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchStatsOnly(), fetchCatalog()]);
            setError(null);
        } catch (err) {
            console.error(err);
            setError('Error cargando los datos de la barra.');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatsOnly = async () => {
        setRefreshing(true);
        try {
            const { data } = await axios.get(`/${slug}/api/events/${eventId}/stats`);
            setStats(data);
        } catch (err) {
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };

    const fetchCatalog = async () => {
        try {
            const { data } = await axios.get(`/${slug}/api/products`);
            setCatalog(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleStockSubmit = async (e) => {
        e.preventDefault();
        if (!selectedProduct || !addQuantity || addQuantity <= 0) return;

        setSubmittingStock(true);
        try {
            await axios.post(`/${slug}/api/events/${eventId}/stock`, {
                productId: selectedProduct,
                quantity: Number(addQuantity)
            });
            setIsModalOpen(false);
            setAddQuantity('');
            setSelectedProduct('');
            fetchStatsOnly(); // Actualizar stats para ver el nuevo stock
        } catch (err) {
            alert('Error cargando stock: ' + (err.response?.data?.error || err.message));
        } finally {
            setSubmittingStock(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-3xl text-center text-red-400">
                    {error}
                </div>
            </div>
        );
    }

    const { totalRevenue = 0, revenueByMethod = {}, salesCount = 0, stockReport = [] } = stats || {};

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl animate-fade-in-up">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <button
                        onClick={() => navigate(`/${slug}/admin/events`)}
                        className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-4"
                    >
                        <ChevronLeft size={20} /> <span className="text-sm font-bold">Volver a Eventos</span>
                    </button>
                    <div className="flex items-center gap-3">
                        <BarChart3 className="text-primary-orange" size={28} />
                        <h1 className="text-4xl font-black text-white px-2 tracking-tight">Reporte de Barra</h1>
                    </div>
                    <p className="text-white/40 font-medium px-2 mt-2">
                        Monitoreo en vivo de ventas e inventario para este evento.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchStatsOnly}
                        disabled={refreshing}
                        className="flex items-center justify-center w-12 h-12 bg-white/5 border border-white/10 rounded-2xl text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                    >
                        <RefreshCw size={20} className={refreshing ? 'animate-spin text-primary-orange' : ''} />
                    </button>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center justify-center gap-2 bg-brand-gradient px-6 py-3 rounded-2xl font-black shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all text-sm h-12"
                    >
                        <Package size={18} /> CARGAR INVENTARIO
                    </button>
                </div>
            </div>

            {/* METRICS GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-glass p-6 rounded-[32px] border-white/5 shadow-xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
                        <DollarSign size={64} />
                    </div>
                    <h4 className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Recaudación Barra</h4>
                    <p className="text-4xl font-black text-green-400">${totalRevenue.toLocaleString()}</p>
                </div>

                <div className="bg-glass p-6 rounded-[32px] border-white/5 shadow-xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
                        <TrendingUp size={64} />
                    </div>
                    <h4 className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Transacciones</h4>
                    <p className="text-4xl font-black text-white">{salesCount}</p>
                </div>

                <div className="bg-glass p-6 rounded-[32px] border-white/5 shadow-xl relative overflow-hidden group">
                    <h4 className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-4">Efectivo vs Digital</h4>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center text-sm font-bold">
                            <span className="text-white/60">Efectivo</span>
                            <span className="text-green-400">${revenueByMethod.cash?.toLocaleString() || 0}</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-green-400" style={{ width: `${totalRevenue ? ((revenueByMethod.cash || 0) / totalRevenue) * 100 : 0}%` }}></div>
                        </div>
                    </div>
                </div>

                <div className="bg-glass p-6 rounded-[32px] border-white/5 shadow-xl relative overflow-hidden group">
                    <h4 className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-4">Mercado Pago / Tarjeta</h4>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center text-sm font-bold">
                            <span className="text-white/60">Digital</span>
                            <span className="text-blue-400">${((revenueByMethod.mp || 0) + (revenueByMethod.card || 0)).toLocaleString()}</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400" style={{ width: `${totalRevenue ? (((revenueByMethod.mp || 0) + (revenueByMethod.card || 0)) / totalRevenue) * 100 : 0}%` }}></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* STOCK / INVENTORY AND CHART */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* CHART: BEBIDAS MAS VENDIDAS */}
                <div className="bg-glass p-8 rounded-[40px] border-white/5 shadow-2xl lg:col-span-2">
                    <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2">
                        <TrendingUp className="text-primary-orange" size={20} />
                        Bebidas más vendidas
                    </h3>
                    {stockReport.length === 0 ? (
                        <div className="h-[300px] flex items-center justify-center text-white/20">
                            No hay datos de inventario todavía
                        </div>
                    ) : (
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stockReport.sort((a, b) => b.sold - a.sold).slice(0, 10)}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fill: '#ffffff40', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                    <Tooltip cursor={{ fill: '#ffffff05' }} contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #ffffff10', borderRadius: '16px' }} />
                                    <Bar dataKey="sold" name="Vendidos" radius={[8, 8, 0, 0]}>
                                        {stockReport.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#FF8C00' : '#ffffff20'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* INVENTORY TABLE */}
                <div className="bg-glass rounded-[40px] border-white/5 shadow-2xl overflow-hidden flex flex-col h-[400px]">
                    <div className="p-6 border-b border-white/5 flex items-center gap-2 bg-white/[0.01]">
                        <Archive className="text-blue-400" size={20} />
                        <h3 className="text-lg font-black text-white">Stock Actual</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {stockReport.length === 0 ? (
                            <div className="text-center text-white/20 pt-10 text-sm">Sin inventario.<br />Hacé clic en Cargar.</div>
                        ) : (
                            stockReport.map((item, i) => {
                                const isLow = item.current <= (item.initial * 0.2); // Alerta si queda 20% o menos
                                return (
                                    <div key={i} className={`p-4 rounded-2xl border flex items-center justify-between ${isLow ? 'bg-red-500/10 border-red-500/20' : 'bg-white/5 border-white/5'}`}>
                                        <div>
                                            <h4 className="font-black text-sm text-white max-w-[150px] truncate" title={item.name}>{item.name}</h4>
                                            <p className="text-[10px] text-white/40 font-bold uppercase mt-1">
                                                Vendido: {item.sold}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-2xl font-black ${isLow ? 'text-red-400' : 'text-white'}`}>{item.current}</p>
                                            {isLow && <span className="text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center justify-end gap-1"><AlertTriangle size={10} /> Bajo</span>}
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL AGREGAR STOCK */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-[#121212] border border-white/10 rounded-[32px] w-full max-w-md p-8 shadow-2xl relative">
                        <h2 className="text-2xl font-black text-white mb-2">Cargar Inventario</h2>
                        <p className="text-white/40 text-sm mb-6">Asigná mercadería a este evento. Esto actualizará el POS de los cajeros instantáneamente.</p>

                        <form onSubmit={handleStockSubmit} className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Producto de la Barra</label>
                                <select
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-orange transition-colors"
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    required
                                >
                                    <option value="">Seleccionar bebida/combo...</option>
                                    {catalog.map(p => (
                                        <option key={p._id} value={p._id}>{p.name} - ${p.price}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Cantidad a sumarle a la barra</label>
                                <input
                                    type="number"
                                    min="1"
                                    required
                                    value={addQuantity}
                                    onChange={(e) => setAddQuantity(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-primary-orange transition-colors text-2xl font-black text-center"
                                    placeholder="Ej: 50"
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 bg-white/5 rounded-xl font-bold text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingStock}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-gradient rounded-xl font-bold text-white shadow-lg shadow-orange-500/20 active:scale-95 transition-all disabled:opacity-50"
                                >
                                    {submittingStock ? <RefreshCw size={18} className="animate-spin" /> : <Plus size={18} />}
                                    Cargar Drop
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventBarDashboard;
